<template>
  <div
    class="e-horizon-typeahead"
    :class="{ active: open }"
  >
    <div
      class="input-container"
      :class="{ visible: open, 'is-set': isValueSet }"
    >
      <div class="label">{{ label }}</div>
      <div
        v-if="!open"
        v-dompurify-html="getNewPlaceholder"
        class="placeholder"
      />
      <input
        ref="input"
        type="text"
        :placeholder="getPlaceholder"
        :autocorrect="false"
        autocomplete="new-password"
        :spellcheck="false"
        @focus="onFocus"
        @keyup="updateFilter"
        @keydown.down.prevent="typeAheadDown"
        @keydown.up.prevent="typeAheadUp"
        @keydown.enter.prevent="doAutoSelect"
      />
      <input
        class="input-typeahead"
        type="text"
        disabled
        :placeholder="firstMatch"
        :autocorrect="false"
        :spellcheck="false"
        autocomplete="new-password"
      />
    </div>

    <div
      v-if="open"
      class="dropdown-menu"
      :class="{ visible: open, empty: isOptionsEmpty }"
    >
      <modal-header
        class="modal-header"
        :is-mobile="isMobile"
        :label="label"
        @closed="close"
      />
      <div
        ref="dropdownContent"
        class="dropdown-content"
      >
        <template v-if="isLabelsVisible">
          <label-cloud
            ref="labelCloud"
            :labels="labels"
            :selected="selectedLabel"
            @click="labelClicked"
          />
          <div
            v-if="showLabelsDisclaimer"
            class="label-disclaimer"
          >
            {{ labelDisclaimer }}
          </div>
        </template>

        <template v-if="showAnyDestinationButton">
          <button-text-circle
            :url="anyDestinationButtonData.data.href"
            :text="anyDestinationButtonData.data.text"
            :circle-text="anyDestinationButtonData.circleText || 'NEW'"
            target="_self"
            @clicked="anyDestinationGAPush"
          />
        </template>

        <template v-if="isRecentSearchVisible">
          <recent-search
            :recent-searches="recentSearches"
            @selectItem="selectResentSearch"
          />
        </template>

        <template v-if="isOptionsEmpty">
          <div class="dropdown-item-empty">
            <template v-if="invalidInputLang">
              {{ translationInvalidLanguage }}
            </template>
            <template v-else>
              {{ translationNoResults }}
              <span class="filter">{{ filter }}</span>
            </template>
          </div>
        </template>

        <ul
          v-else
          ref="dropdownMenu"
          class="dropdown-items"
          :class="{ labels: isLabelsVisible }"
        >
          <template v-for="(option, index) in options">
            <li
              v-if="isHeader(option)"
              :key="index"
              class="dropdown-header"
            >
              {{ option }}
            </li>

            <li
              v-else
              :key="index"
              class="dropdown-item"
              :class="{
                highlight: index === typeAheadPointer,
                selected: option.aptWithCode === value.aptWithCode
              }"
              @click="updateValue(option)"
            >
              <slot>
                <item
                  :touched="touched"
                  :filter="filter"
                  :value="option"
                />
              </slot>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
  import debounce from 'lodash-es/debounce'
  import isEmpty from 'lodash-es/isEmpty'
  import isString from 'lodash-es/isString'
  import ModalHeader from 'components/eHorizon/Modal/ModalHeader'
  import PointerScrollMixin from 'components/Typeahead/mixins/PointerScrollWithLabels'
  import TypeaheadPointerMixin from 'components/Typeahead/mixins/TypeaheadPointer'
  import Item from 'components/eHorizon/Typeahead/Item'
  import LabelCloud from 'components/eHorizon/LabelCloud'
  import OrigDestUtils from 'store/utils/eHorizonOrigDestUtils'
  import { mapState } from 'vuex'
  import gtmFSF from 'services/AirBaltic/GTM/FSF'

  export default {
    components: {
      LabelCloud,
      ModalHeader,
      Item,
      'recent-search': () =>
        import(
          /* webpackChunkName: "recent-search-input" */ 'components/eHorizon/RecentSearch/RecentSearchInsideInput'
        ),
      'button-text-circle': () =>
        import(
          /* webpackChunkName: "button-text-circle" */ 'components/Buttons/TextCircle'
        )
    },
    mixins: [PointerScrollMixin, TypeaheadPointerMixin],
    props: {
      filter: {
        type: String,
        required: true
      },
      touched: {
        type: Boolean,
        required: false,
        default: false
      },
      value: {
        type: Object,
        required: true
      },
      options: {
        type: Array,
        required: true
      },
      open: {
        type: Boolean,
        required: true
      },
      placeholder: {
        type: String,
        required: true
      },
      label: {
        type: String,
        required: false,
        default: ''
      },
      choose: {
        type: String,
        required: true
      },
      translationNoResults: {
        type: String,
        required: true
      },
      translationInvalidLanguage: {
        type: String,
        required: true
      },
      invalidInputLang: {
        type: Boolean,
        default: false
      },
      maxEntries: {
        type: Number,
        default: 4
      },
      isMobile: {
        type: String,
        required: true
      },
      labels: {
        type: Object,
        default: () => {},
        required: false
      },
      showLabelsDisclaimer: {
        type: Boolean,
        default: false
      },
      selectedLabel: {
        type: String,
        default: '',
        required: false
      },
      altPlaceholder: {
        type: Boolean,
        required: false,
        default: false
      },
      recentSearches: {
        type: Array,
        default: () => []
      },
      anyDestinationButtonData: {
        type: Object,
        required: false,
        default: () => {}
      },
      isBtOriginAirport: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        firstMatch: ''
      }
    },
    computed: {
      ...mapState('eHorizonSearchForm', ['origin']),
      getPlaceholder() {
        if (!this.open) {
          return ''
        }

        return this.choose
      },
      getNewPlaceholder() {
        if (!isEmpty(this.value) && !this.open) {
          if (this.altPlaceholder) {
            return OrigDestUtils.toCity(this.value)
          }

          return OrigDestUtils.toFSFLabel(this.value)
        }

        return this.choose
      },
      isLabelsVisible() {
        return !isEmpty(this.labels) && this.touched
      },
      isRecentSearchVisible() {
        return (
          this.touched && this.recentSearches.length && this.selectedLabel === 'label_all'
        )
      },
      isOptionsEmpty() {
        return this.options.length === 0
      },
      isValueSet() {
        return !isEmpty(this.value)
      },
      hasScrollbar() {
        return this.options.length > this.maxEntries
      },
      overflowYValue() {
        if (this.isMobile === 'mobile') {
          return this.hasScrollbar ? 'scroll' : 'hidden'
        }

        return 'unset'
      },
      labelDisclaimer() {
        return this.$t('searchForm.originDestin.labelDisclaimer')
      },
      showAnyDestinationButton() {
        return this.anyDestinationButtonData?.data?.href && this.isBtOriginAirport
      }
    },
    watch: {
      open(isOpen) {
        if (isOpen) {
          this.scrollToTop()
        } else {
          this.$refs.input.value = ''
          this.$emit('update:filter', '')
        }

        this.clearFirstMatch()
      }
    },
    methods: {
      isHeader(option) {
        return isString(option)
      },

      labelClicked(key) {
        this.$emit('update:label', key)
      },

      updateFilter(e) {
        if (e.keyCode !== 9 && e.keyCode !== 16) {
          this.$emit('update:filter', e.target.value)
          this.findFirstMatch(e.target.value)
        }
      },

      updateValue(option) {
        this.clearFirstMatch()
        this.$emit('update:value', option)
      },

      blur() {
        this.blurInput()
      },

      hasFocus() {
        return this.$refs.input === document.activeElement
      },

      blurInput() {
        if (this.isMobile !== 'mobile') {
          this.$refs.input.blur()
        }
      },

      focusInput() {
        this.$refs.input.focus()
      },

      focus() {
        this.focusInput()
      },

      onFocus() {
        this.$refs.input.focus()
        this.selectInputContent()
        this.$emit('focused')
      },

      close() {
        this.clearFirstMatch()
        this.$emit('update:open', false)
      },

      clearFirstMatch() {
        this.firstMatch = ''
      },

      findFirstMatch: debounce(function (filterValue) {
        const firstAirport = this.options.find((option) => !isString(option))

        if (isEmpty(firstAirport) || isEmpty(filterValue)) {
          this.clearFirstMatch()
        } else {
          this.firstMatch = firstAirport.aptWithCode
            .toLowerCase()
            .startsWith(filterValue.toLowerCase())
            ? firstAirport.aptWithCode
            : ''

          for (let i = 0; i < filterValue.length; i++) {
            const char = filterValue.charAt(i)
            const firstCharFormat =
              char === char.toUpperCase()
                ? this.firstMatch.charAt(i).toUpperCase()
                : this.firstMatch.charAt(i).toLowerCase()

            this.firstMatch = this.setCharAt(this.firstMatch, i, firstCharFormat)
          }
        }
      }, 1),
      setCharAt(str, index, chr) {
        if (index > str.length - 1) return str

        return str.substr(0, index) + chr + str.substr(index + 1)
      },
      doAutoSelect() {
        if (!isEmpty(this.firstMatch) && this.typeAheadPointer === -1) {
          this.updateValue(this.options.find((option) => !isString(option)))
        } else {
          this.typeAheadSelect()
        }
        this.blurInput()
      },
      scrollToTop: debounce(function () {
        if (this.$refs.dropdownContent) {
          this.$refs.dropdownContent.scrollTop = 0

          if (this.isMobile === 'mobile') {
            document.body.scrollTop = 0
            document.documentElement.scrollTop = 0
          }
        }
      }, 10),
      selectInputContent: debounce(function () {
        this.scrollToTop()
        if (this.isMobile !== 'mobile' && this.$refs.input) {
          this.$refs.input.setSelectionRange(0, 9999)
        }
      }, 1),

      anyDestinationGAPush() {
        this.$emit('anyDestinationSelected')
      },
      selectResentSearch(item) {
        gtmFSF.flightSearch('recent_search', item)
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .e-horizon-typeahead {
    justify-content: center;
    background: #ffffff;

    @include bp-mf-desktop() {
      display: block;
      position: relative;
    }

    .input-container {
      height: 56px;
      position: relative;

      &.visible {
        position: fixed;
        width: 80%;
        top: 19px;
        left: 15px;
        z-index: 5;

        @include bp-mf-tablet {
          position: absolute;
          width: 250px;
          top: -56px;
          left: calc(50% - 150px);
          z-index: 5;
        }

        @include bp-mf-desktop {
          width: 400px;
          top: 0;
          left: -11px;
        }

        .label {
          font-size: rem(12, 16);

          @include bp-mf-desktop {
            display: none;
          }
        }

        input {
          font-size: rem(16, 16);

          @include bp-mf-desktop {
            top: 5px;
            left: 24px;
            height: 52px;
            width: 350px;
          }
        }
      }

      @include bp-mf-desktop {
        position: static;
        height: 56px;
      }

      input {
        @include text-overflow();
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 0;
        background: none;

        &.input-typeahead {
          z-index: -1;
        }

        @include input-placeholder {
          text-transform: none;
        }

        @include bp-mf-desktop {
          top: 12px;
          left: 12px;
          height: 44px;
          width: calc(100% - 10px);
        }
      }

      .label {
        @include text-overflow();
        position: absolute;
        top: 7px;
        left: 10px;
        z-index: 0;
        color: $blue-secondary;
        font-size: rem(11, 16);
        line-height: 14px;

        @include bp-mf-desktop {
          top: 8px;
          left: 12px;
          max-width: 210px;
        }
      }

      .placeholder {
        position: absolute;
        color: $blue-secondary;
        font-size: 16px;
        top: 25px;
        left: 12px;

        ::v-deep {
          b {
            font-weight: 500;
            color: $blue-dark;
          }
        }
      }
    }

    &.destin > .visible {
      @include bp-mf-tablet {
        top: -134px;
      }

      @include bp-mf-desktop {
        top: -12px;
      }

      &.input-container {
        @include bp-mf-tablet {
          top: -116px;
        }

        @include bp-mf-desktop {
          top: 0;
        }
      }
    }

    ::v-deep .recent-search-block .title,
    ::v-deep .dropdown-header {
      color: $blue-medium;
      padding: 0 24px;
      height: 32px;
      line-height: 32px;
      display: flex;
      align-items: center;
      font-weight: 500;
      font-size: rem(13, 16);
    }

    .dropdown-menu {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 4;
      width: 100%;
      background: #ffffff;

      &.visible {
        height: 100%;

        @include bp-mf-tablet {
          height: auto;
          border-radius: 6px;
        }
      }

      @include bp-mf-tablet {
        width: 320px;
        position: absolute;
        top: -74px;
        left: calc(50% - 160px);
        height: 72px;
        box-shadow: -1px 0 0 0 rgba(black, 0.1), 0 2px 10px 0 rgba(black, 0.16);
        border-radius: 6px 6px 0 0;
      }

      @include bp-mf-desktop() {
        display: block;
        width: 400px;
        height: unset;
        position: absolute;
        top: -12px;
        left: -11px;
        border-radius: 3px;
      }

      .dropdown-content {
        width: 100%;
        height: calc(100% - 80px);
        overflow-x: hidden;
        position: fixed;
        top: 80px;

        @include bp-mf-tablet() {
          position: static;
          max-height: 413px;
          overflow-y: auto;
          background: #ffffff;
          border-radius: 0 0 6px 6px;
        }

        @include bp-mf-desktop() {
          max-height: 413px;
          height: auto;
          overflow-y: auto;
          width: 400px;
        }

        .dropdown-items {
          width: 100%;
          height: auto;
          max-height: unset;
          position: relative;

          &.labels {
            @include bp-mf-tablet() {
              border-radius: 0 0 6px 6px;
            }
          }

          @include bp-mf-tablet() {
            height: auto;
            border-radius: 0 0 6px 6px;
          }
        }

        .dropdown-header {
          z-index: 1;
          font-size: rem(13, 16);
          background: $white-dropdown-header;
          color: $blue-medium;
          padding: 0 24px;
          height: 32px;
          display: flex;
          align-items: center;
          @include text-overflow();
          font-weight: 500;
          position: sticky;
          top: 0;
        }

        .dropdown-item-empty {
          padding: 21px 24px;
          color: $input-placeholder;
          font-size: rem(16, 16);
          line-height: 22px;
          word-wrap: break-word;

          .filter {
            color: $grey-default;
          }
        }

        .dropdown-item {
          cursor: pointer;
          display: flex;
          align-items: center;
          padding: 0 24px;
          height: 56px;
          transition: all 0.2s ease;
          position: relative;

          &:hover,
          &.selected,
          &.highlight {
            color: $blue-medium;
            background-color: $gray-marble;

            ::v-deep .airport {
              color: $blue-medium;
            }
          }

          ::v-deep {
            .bt-flight {
              top: 12px;
              right: 25px;
              position: absolute;
              color: $grey;
              font-size: rem(12, 16);
              padding-right: 17px;

              @include bp-mf-desktop() {
                right: 27px;
              }

              &:after {
                top: 2px;
                right: 0;
                font-family: $icon-font-family;
                content: $icon-plane;
                position: absolute;
                font-size: rem(13, 16);
                color: $green-base;
                pointer-events: none;
                transform: rotate(45deg);
              }
            }
          }
        }
      }
    }
  }

  .label-disclaimer {
    padding: 0 16px 12px 24px;
    color: $input-placeholder;
    font-size: 11px;
  }
</style>
